
import { Component, Prop, Vue, Watch } from "vue-property-decorator"
import WorkReportQuery from "@/graphql/workreport/workreport.graphql"
import GroupQuery from "@/graphql/group/group.graphql"
import * as cdn from "@coredination/components"
import DeleteWorkReport from "@/graphql/workreport/deleteworkreport.graphql"
import EntityEventSubscription from "@/graphql/subscription/entityevent.graphql"

import { CircularLoader, PullRefresh } from "@coredination/components"
import { Formatting } from "@/util/formatting"
import { navbar, NavbarOption } from "@/components/NavbarOptions.vue"

const dateFormat = new Formatting.DateFormatting()

@Component({
    apollo: {
        workReport: {
            query: WorkReportQuery,
            skip() {
                return !this.uuid
            },
            variables() {
                return { uuid: this.uuid }
            },
        },
        me: cdn.graphql.me,
        group: {
            query: GroupQuery,
            skip() {
                return this.model.groupName || !this.model.groupUuid
            },
            variables() {
                return { uuid: this.model.groupUuid }
            },
        },
        $subscribe: {
            entityEvent: {
                query: EntityEventSubscription,
                result(result: any) {
                    const event = result.data.entityEvent
                    if (
                        event.type == "entityUpdated" &&
                        event.data &&
                        event.data.entityClass == "WorkReport" &&
                        event.data.uuid == this.model.uuid
                    ) {
                        console.log("work report was updated", event.data)
                        if (this.workReport.version >= event.data.version) {
                            console.log("not refreshing - same version")
                        } else {
                            console.log("refreshing")
                            this.refresh()
                        }
                    }
                },
                skip() {
                    return this.inhibitSubscription
                },
            },
        },
    },
    components: {
        PullRefresh,
        CircularLoader,
        // form fields
        ItemsTable: () => import("@/components/misc/ItemsTable.vue"),
        LabelField: () => import("@/components/fields/LabelField.vue"),
        VehicleField: () => import("@/components/fields/VehicleField.vue"),
        CustomFieldsField: () => import("@/components/fields/CustomFieldsField.vue"),
        DeleteDialog: () => import("@/components/dialogs/DeleteDialog.vue"),
    },
})
export default class WorkReportView extends Vue {
    @Prop()
    uuid?: string

    me: any = {}
    group: any = {}
    workReport: any = { startTime: null, endTime: null }
    showDeleteModal: boolean = false

    get model() {
        return this.workReport
    }

    get editable(): boolean {
        return (
            this.me &&
            this.me.permissions &&
            (this.me.permissions.includes("edit_all_data") || this.me.permissions.includes("report_work")) &&
            !this.attested &&
            !this.hasBeenInvoiced &&
            !this.lockFinished &&
            (this.hasGroupPermission("alter_work_reports") || this.me?.uuid == this.workReport?.userUuid)
        )
    }

    @Watch("workReport")
    @Watch("editable")
    watchEditable() {
        navbar?.setOptions(this.options)
    }

    get hasBeenInvoiced(): boolean {
        return this.workReport?.invoicedTimeStamp
    }

    get attested(): boolean {
        return this.workReport && this.workReport.attestedTimeStamp != null
    }

    get lockFinished(): boolean {
        return (
            this.groupConfiguration?.lock_finished_reports &&
            this.workReport?.finished &&
            !this.hasGroupPermission("alter_work_reports")
        )
    }

    get inhibitSubscription() {
        return localStorage && !!localStorage.getItem("inhibitSubscription")
    }

    mounted() {
        navbar?.setOptions(this.options)
    }

    clickDelete() {
        this.showDeleteModal = !this.showDeleteModal
        // this.$router.back()
    }

    confirmDelete() {
        var self: any = this
        var uuid: string = this.model.uuid
        return this.$apollo
            .mutate({
                mutation: DeleteWorkReport,
                variables: { uuid },
                update(proxy, response) {
                    self.invalidateCache(proxy, response, true)
                },
            })
            .then(async (response) => {
                console.log("Workreport deleted", response)
                self.$router.back()
            })
    }

    get loading() {
        return (
            this.$apollo.queries.workReport.loading ||
            this.$apollo.queries.me.loading ||
            this.$apollo.queries.group.loading
        )
    }

    refresh() {
        console.log("refreshing work report form")
        if (this.$apollo.queries.workReport && typeof this.$apollo.queries.workReport.refetch == "function")
            this.$apollo.queries.workReport.refetch()
        if (this.$apollo.queries.me && typeof this.$apollo.queries.me.refetch == "function")
            this.$apollo.queries.me.refetch()
        if (this.$apollo.queries.group && typeof this.$apollo.queries.group.refetch == "function")
            this.$apollo.queries.group.refetch()
    }

    hasMultipleActiveGroups() {
        return this.me && this.me.groupMemberships && this.me.groupMemberships!.filter((m: any) => m.active).length > 1
    }

    get options(): NavbarOption[] {
        if (!this.me || !this.me.permissions || this.model.deleted) return []
        var options = []
        if (this.editable) {
            options.push({
                icon: "edit",
                to: `/workreport/${this.uuid}/edit`
            })
            options.push({
                title: this.$t("workReportOptions_deleteWorkReport"),
                action: this.clickDelete,
            })
        }
        return options
    }

    get groupConfiguration(): any {
        if (!this.group || !this.model || !this.model.groupUuid || !this.group.configuration) return false
        return JSON.parse(this.group.configuration)
    }

    hasGroupPermission(permission: string) {
        var membership = this.getGroupMembershipForReport()
        if (membership && membership.permissions) {
            return membership.permissions.includes(permission)
        }
        return false
    }

    getGroupMembershipForReport() {
        if (this.me?.groupMemberships && this.workReport?.groupUuid) {
            return this.me.groupMemberships.find((membership: any) => {
                return membership.groupUuid === this.workReport.groupUuid
            })
        }
        return null
    }

    invalidateCache(proxy: any, response: any, job?: boolean) {
        const hackProxy = proxy as any
        Object.keys(hackProxy.data.data).forEach((key) => {
            if (key.match(/(workreport|myWorkReports|myWorkStats|workReportsForJob)/)) hackProxy.data.delete(key)
            if (job && key.match(/(myJobs|myWorkStats|currentWorkReportForJob)/)) hackProxy.data.delete(key)
        })
    }

    get titleExtended(): string {
        var title: string = ""
        if (this.model.title) title = this.model.title
        else if (this.model.jobTitle) title = this.model.jobTitle
        else if (this.model.assetName) {
            title = (this.model.assetNo ? this.model.assetNo + " - " : "") + this.model.assetName
            if (this.model.activityName) title = this.model.activityName + ": " + title
        } else if (this.model.customerName) {
            title = this.model.customerName
            if (this.model.activityName) title = this.model.activityName + ": " + title
            if (this.model.customerProjectNo || this.model.customerProjectName) {
                title += ":"
                if (this.model.customerProjectNo) title += " " + this.model.customerProjectNo
                if (this.model.customerProjectName) title += " " + this.model.customerProjectName
            }
        } else if (this.model.activityName) title = this.model.activityName
        return title
    }
}
